import { Linking, Pressable, ScrollView, Text, View } from "react-native";
import { useRouter } from "expo-router";
import {
  FileIcon,
  FileTextIcon,
  FolderPlusIcon,
  HashIcon,
  ImageIcon,
  LinkIcon,
  MoreHorizontalIcon,
  MoreVerticalIcon,
  PlusIcon,
} from "lucide-react-native";
import { observer } from "mobx-react-lite";

import { showConfirmation } from "~/components/confirmation/showConfirmation";
import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { useCurrentSpace } from "~/hooks/useCurrentSpace";
import { icons } from "~/lib/constants";
import { cn } from "~/lib/utils";
import { useAccountStore } from "~/stores/space";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";
import { ListContainer, ListContainerTitleText, ListItem } from "~/ui/List";
import { useAccountActions } from "../AccountActions/context";
import { Logo } from "../Logo";
import {
  BACKLOG_SLUG,
  encodeFilterForURL,
  SAVE_TYPE_OPTIONS,
  useParams,
} from "./params";

export const WorkspaceMenu = observer(function WorkspaceMenu(props: {
  className?: string;
}) {
  const params = useParams();
  const { collectionId, tagId } = params;

  const { space, account } = useCurrentSpace();

  const { store } = useAccountStore();
  const accountActions = useAccountActions();

  return (
    <View className="gap-y-8">
      <ListContainer>
        <ListItem
          className={cn("border-r-4 border-transparent")}
          href={{
            pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
            params: {
              account: account.slug,
              space: space.slug,
            },
          }}
        >
          <View className="flex-row items-center gap-2">
            <Logo width={18} height={18} />
            <Text className="text-base text-white">All Saves</Text>
          </View>
          <Text className="text-text-secondary text-base">
            {space.savesCount}
          </Text>
        </ListItem>
        {SAVE_TYPE_OPTIONS.map((type) => {
          const saveCount = space.savesAsArray.filter(
            (save) => save.type === type.type,
          ).length;

          return (
            <ListItem
              key={type.type}
              className={cn("border-r-4 border-transparent")}
              href={{
                pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                params: {
                  account: account.slug,
                  space: space.slug,
                  filters: encodeFilterForURL({
                    collection: {
                      ids: [],
                      mode: "is",
                    },
                    tag: {
                      ids: [],
                      mode: "is",
                    },
                    saveType: {
                      ids: [type.type],
                      mode: "is",
                    },
                  }),
                },
              }}
            >
              <View className="flex-row items-center gap-2">
                {type.icon === "link" && (
                  <LinkIcon size={18} className="text-text-secondary" />
                )}
                {type.icon === "file" && (
                  <FileIcon size={18} className="text-text-secondary" />
                )}
                {type.icon === "file-text" && (
                  <FileTextIcon size={18} className="text-text-secondary" />
                )}
                {type.icon === "image" && (
                  <ImageIcon size={18} className="text-text-secondary" />
                )}

                <Text className="text-base text-white">{type.title}</Text>
              </View>
              <Text className="text-text-secondary text-base">{saveCount}</Text>
            </ListItem>
          );
        })}
      </ListContainer>

      {space.collections.size > 0 && (
        <ListContainer
          className="group/container"
          title={
            <View className="flex-row items-center">
              <View className="flex-row gap-2">
                <FolderPlusIcon size={18} className="text-text-secondary" />
                <ListContainerTitleText>Collections</ListContainerTitleText>
              </View>

              <Pressable
                onPress={() => {
                  accountActions.openAddCollection({
                    spaceId: space.id,
                  });
                }}
                className="bg-surface-secondary ml-auto rounded-sm p-1 opacity-0 group-hover/container:opacity-100"
              >
                <PlusIcon size={18} className="text-text-primary" />
              </Pressable>
            </View>
          }
        >
          {space.collectionsAsArray.map((collection) => {
            const saveCount = space.savesAsArray.filter((save) =>
              save.collections.has(collection.id),
            ).length;

            return (
              <ListItem
                className={cn(
                  "group gap-2 border-r-4 border-transparent",
                  collectionId === collection.id && "border-button-primary",
                )}
                key={collection.id}
                href={{
                  pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                  params: {
                    account: account.slug,
                    space: space.slug,
                    filters: encodeFilterForURL({
                      collection: {
                        ids: [collection.id],
                        mode: "is",
                      },
                      tag: {
                        ids: [],
                        mode: "is",
                      },
                    }),
                  },
                }}
              >
                <View className="flex-row items-center">
                  <Text className="text-base text-white">
                    {collection.title}
                  </Text>
                </View>
                <Text className="text-text-secondary ml-auto inline-block text-base group-hover:hidden group-has-[[aria-expanded=true]]:hidden">
                  {saveCount}
                </Text>
                <>
                  <Dropdown
                    asChild
                    trigger={
                      <Pressable
                        role="button"
                        className="hidden group-hover:flex group-has-[[aria-expanded=true]]:flex"
                      >
                        <Text className="text-text-primary">
                          <MoreVerticalIcon size={18} />
                        </Text>
                      </Pressable>
                    }
                    groups={[
                      {
                        items: [
                          {
                            icon: icons.edit,
                            title: "Edit collection",
                            key: "edit",
                            onSelect: () => {
                              accountActions.openEditCollection({
                                collection,
                                space,
                              });
                            },
                          },

                          {
                            icon: icons.delete,
                            title: "Delete collection",
                            key: "delete",
                            destructive: true,
                            onSelect: () => {
                              showConfirmation({
                                onConfirm: () => {
                                  store.deleteCollection({
                                    spaceId: space.id,
                                    collectionId: collection.id,
                                  });
                                },
                              });
                            },
                          },
                        ] as const,
                      },
                    ]}
                  />
                </>
              </ListItem>
            );
          })}
        </ListContainer>
      )}

      {space.tags.size > 0 && (
        <ListContainer
          className="group/container"
          title={
            <View className="flex-row items-center">
              <View className="flex-row gap-2">
                <HashIcon size={18} className="text-text-secondary" />
                <ListContainerTitleText>Tags</ListContainerTitleText>
              </View>
              <Pressable
                onPress={() => {
                  accountActions.openAddTag({
                    spaceId: space.id,
                  });
                }}
                className="bg-surface-secondary ml-auto rounded-sm p-1 opacity-0 group-hover/container:opacity-100"
              >
                <PlusIcon size={18} className="text-text-primary" />
              </Pressable>
            </View>
          }
        >
          {space.tagsAsArray.map((tag) => {
            const saveCount = space.savesAsArray.filter((save) =>
              save.tags.has(tag.id),
            ).length;

            return (
              <ListItem
                className={cn(
                  "group gap-2 border-r-4 border-transparent",
                  tagId === tag.id && "border-button-primary border-r-4",
                )}
                key={tag.id}
                href={{
                  pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                  params: {
                    account: account.slug,
                    space: space.slug,
                    filters: encodeFilterForURL({
                      collection: {
                        ids: [],
                        mode: "is",
                      },
                      tag: {
                        ids: [tag.id],
                        mode: "is",
                      },
                    }),
                  },
                }}
              >
                <View className="flex-row items-center">
                  <Text className="text-base text-white">{tag.value}</Text>
                </View>
                <Text className="text-text-secondary ml-auto inline-block text-base group-hover:hidden group-has-[[aria-expanded=true]]:hidden">
                  {saveCount}
                </Text>
                <>
                  <Dropdown
                    asChild
                    trigger={
                      <Pressable
                        role="button"
                        className="hidden group-hover:flex group-has-[[aria-expanded=true]]:flex"
                      >
                        <Text className="text-text-primary">
                          <MoreVerticalIcon size={18} />
                        </Text>
                      </Pressable>
                    }
                    groups={[
                      {
                        items: [
                          {
                            icon: icons.edit,
                            title: "Edit tag",
                            key: "edit",
                            onSelect: () => {
                              accountActions.openEditTag({
                                tag: tag,
                                space,
                              });
                            },
                          },

                          {
                            icon: icons.delete,
                            title: "Delete tag",
                            key: "delete",
                            destructive: true,
                            onSelect: () => {
                              showConfirmation({
                                onConfirm: () => {
                                  store.deleteTag({
                                    spaceId: space.id,
                                    tagId: tag.id,
                                  });
                                },
                              });
                            },
                          },
                        ] as const,
                      },
                    ]}
                  />
                </>
              </ListItem>
            );
          })}
        </ListContainer>
      )}
    </View>
  );
});

export function WorkspaceActions(props: { trigger?: React.ReactElement }) {
  const { space } = useCurrentSpace();
  const { store } = useAccountStore();
  const account = useCurrentAccount();
  const accountActions = useAccountActions();
  const { replace } = useRouter();
  return (
    <>
      <Dropdown
        asChild
        trigger={
          props.trigger || (
            <Button
              variant="outline"
              size={"small"}
              iconBefore={<MoreHorizontalIcon size={18} />}
              className="invisible group-hover/sidebar:visible group-has-[[aria-expanded=true]]/sidebar:visible"
            />
          )
        }
        groups={[
          {
            items: (
              [
                {
                  key: "Edit",
                  title: "Edit workspace",
                  icon: icons.edit,
                  onSelect: () => {
                    accountActions.openEditSpace({
                      spaceId: space.id,
                    });
                  },
                },

                {
                  key: "newCollection",
                  title: "New collection",
                  icon: icons.collection,
                  onSelect: () => {
                    accountActions.openAddCollection({
                      spaceId: space.id,
                    });
                  },
                },

                {
                  key: "newTag",
                  title: "New tag",
                  icon: icons.tag,
                  onSelect: () => {
                    accountActions.openAddTag({
                      spaceId: space.id,
                    });
                  },
                },

                // {
                //   key: "Share",
                //   title: "Share",
                //   icon: {
                //     ios: "square.and.arrow.up",
                //     web: "share",
                //   },
                // },
                {
                  key: "Delete",
                  title: "Delete",
                  icon: icons.delete,
                  onSelect: () => {
                    showConfirmation({
                      onConfirm: () => {
                        store.deleteSpace({
                          spaceId: space.id,
                        });
                        replace({
                          pathname:
                            "/(app)/(authenticated)/[account]/(account)",
                          params: {
                            account: account.slug,
                          },
                        });
                      },
                    });
                  },
                  destructive: true,
                },
              ] as const
            ).filter((item) => {
              if (space.slug === BACKLOG_SLUG && item.key === "newCollection")
                return false;
              return true;
            }),
          },
        ]}
      />
    </>
  );
}
